// WordPages — a Word document shown as it is (#89, pen E-06): its pages with
// their own layout, fonts and figures, rendered from the original file by
// `docx-preview` (the vendor renderer, loaded only when a Word source opens).
//
// The renderer is the anti-corruption boundary, as pdf.js is for PDFs: it
// paints into this component's own box at the render edge and decides
// nothing. Any failure (a file it can't read, a broken document) falls back
// to `fallback`, the default viewer (E-13), so a Word source is never a
// blank page. View-local state: whether the paint is pending, done or failed.
//
// The referenced passages are marked on the painted pages as in the other
// viewers: the paragraph that holds a reference is tinted and carries its
// numbers in the margin; choosing one reports it up (`onMarker`), and the
// selected one is brought into view. These marks are DOM written into the
// renderer's box after each paint, the same render-edge work.

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { centerInScrollParent } from "../scrollParent.js";
import { paragraphMarksOf, type Reference } from "./sourceViewerModel.js";

type Paint =
    | { readonly type: "word_painting" }
    | { readonly type: "word_painted" }
    | { readonly type: "word_failed" };

const NO_REFERENCES: readonly Reference[] = [];

export const WordPages = ({
    data,
    fallback,
    references = NO_REFERENCES,
    selected,
    onMarker,
}: {
    // The original .docx bytes.
    readonly data: Uint8Array;
    // What to show when the document can't be painted (the E-13 card).
    readonly fallback: ReactNode;
    // The numbered passages to mark, the selected number, and the click.
    readonly references?: readonly Reference[];
    readonly selected?: number;
    readonly onMarker?: (n: number) => void;
}): ReactNode => {
    const { t } = useLingui();
    const box = useRef<HTMLDivElement>(null);
    const [paint, setPaint] = useState<Paint>({ type: "word_painting" });

    useEffect(() => {
        const el = box.current;
        if (el === null) return;
        let cancelled = false;
        setPaint({ type: "word_painting" });
        // The pages keep their real size; a narrow centre shows them scaled
        // down, never cut off (a DOM read/write at the render edge).
        // The page's own width, read once before any scaling applies.
        let pageWidth = 0;
        const fitToWidth = () => {
            const page = el.querySelector<HTMLElement>("section.sv-docx");
            if (page === null) return;
            if (pageWidth === 0) pageWidth = page.offsetWidth;
            if (pageWidth === 0) return;
            const scale = Math.min(1, el.clientWidth / pageWidth);
            el.style.setProperty("--sv-word-scale", String(scale));
        };
        const observer = new ResizeObserver(fitToWidth);
        observer.observe(el);
        // One handled chain: every outcome lands in `paint`, a failure in
        // the fallback (never a floating promise).
        import("docx-preview")
            .then(({ renderAsync }) =>
                cancelled
                    ? undefined
                    : renderAsync(data, el, el, {
                          className: "sv-docx",
                          inWrapper: true,
                          breakPages: true,
                          ignoreLastRenderedPageBreak: true,
                          renderHeaders: true,
                          renderFooters: true,
                          renderFootnotes: true,
                          useBase64URL: true,
                      }),
            )
            .then(() => {
                if (cancelled) return;
                fitToWidth();
                setPaint({ type: "word_painted" });
            })
            .catch(() => {
                if (!cancelled) setPaint({ type: "word_failed" });
            });
        return () => {
            cancelled = true;
            observer.disconnect();
            el.replaceChildren();
        };
    }, [data]);

    // Latest-callback ref: a new `onMarker` identity from the parent never
    // re-marks (and re-scrolls) the pages.
    const onMarkerRef = useRef(onMarker);
    useEffect(() => {
        onMarkerRef.current = onMarker;
    }, [onMarker]);

    // Mark the referenced paragraphs on the painted pages.
    const referencesKey = references.map((r) => `${r.n}:${r.quote}`).join("\n");
    // biome-ignore lint/correctness/useExhaustiveDependencies: `referencesKey` stands for `references` (a fresh array each render).
    useEffect(() => {
        const el = box.current;
        if (el === null || paint.type !== "word_painted") return;
        const paragraphs = [
            ...el.querySelectorAll<HTMLElement>("section.sv-docx p"),
        ];
        const marks = paragraphMarksOf(
            paragraphs.map((p) => p.textContent ?? ""),
            references,
        );
        const marked = [...marks.entries()].flatMap(([i, numbers]) => {
            const p = paragraphs[i];
            if (p === undefined) return [];
            const rail = document.createElement("span");
            rail.className = "sv-word-numbers";
            for (const n of numbers) {
                const button = document.createElement("button");
                button.type = "button";
                button.className = "bilda-pdf-marker";
                button.textContent = String(n);
                button.setAttribute("aria-label", t`Reference ${n}`);
                if (n === selected) button.dataset.selected = "true";
                button.addEventListener("click", () =>
                    onMarkerRef.current?.(n),
                );
                rail.append(button);
            }
            p.classList.add("sv-word-mark");
            if (selected !== undefined && numbers.includes(selected)) {
                p.dataset.selected = "true";
                centerInScrollParent(p);
            }
            p.prepend(rail);
            return [{ p, rail }];
        });
        return () => {
            for (const { p, rail } of marked) {
                rail.remove();
                p.classList.remove("sv-word-mark");
                delete p.dataset.selected;
            }
        };
    }, [paint.type, referencesKey, selected, t]);

    return paint.type === "word_failed" ? (
        fallback
    ) : (
        <div
            ref={box}
            role="document"
            className="sv-word"
            data-painted={paint.type === "word_painted" ? "true" : undefined}
            aria-busy={paint.type === "word_painting"}
            aria-label={t`The original document`}
        />
    );
};
